import * as RadixCollapsible from '@radix-ui/react-collapsible';
import classNames from 'classnames';
import { memo, type ReactNode, useCallback, useState } from 'react';

import { ChevronDownIcon } from '@air/design-icons';
import { IconButton } from '@air/primitive-icon-button';
import { Tooltip } from '@air/primitive-tooltip';
import { tailwindVariants } from '@air/utils-tailwind';

export type CollapsibleCardProps = {
  children: ReactNode;
  header: ReactNode;
  footer?: ReactNode;
  testId?: string;
  variant?: 'default' | 'ghost';
  onOpenChange?: (isExpanded: boolean) => void;
  isDefaultExpanded?: boolean;
  clickableHeader?: boolean;
  className?: string;
};

export const collapsibleCard = tailwindVariants({
  base: 'w-full rounded-4 p-2',
  variants: {
    variant: {
      default: 'ads:border-ads-gray-5 ads:bg-raised bg-surface-1 shadow-kanban dark:border dark:border-gray-5',
      ghost: 'bg-transparent',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

export const CollapsibleCard = memo(
  ({
    testId,
    header,
    footer,
    children,
    variant = 'default',
    onOpenChange,
    className,
    isDefaultExpanded = true,
    clickableHeader = false,
  }: CollapsibleCardProps) => {
    const [isExpanded, setIsExpanded] = useState(isDefaultExpanded);

    const toggleLabel = isExpanded ? 'Collapse' : 'Expand';

    const handleOpenChange = useCallback(
      (isExpanded: boolean) => {
        setIsExpanded(isExpanded);
        onOpenChange?.(isExpanded);
      },
      [onOpenChange],
    );

    const handleToggle = useCallback(() => {
      handleOpenChange(!isExpanded);
    }, [handleOpenChange, isExpanded]);

    return (
      <RadixCollapsible.Root
        className={collapsibleCard({ variant, className })}
        data-testid={testId}
        onOpenChange={handleOpenChange}
        open={isExpanded}
      >
        {clickableHeader ? (
          <button
            className={classNames(
              'flex h-10 w-full items-center justify-between rounded-8 px-4 text-left ads:hover:bg-hover-subtle hover:bg-gray-5 active:bg-gray-4 ads:active:bg-hover-subtle',
              isExpanded && 'hover:rounded-b-none',
            )}
            onClick={handleToggle}
            aria-label={toggleLabel}
            type="button"
          >
            {header}
            <ChevronDownIcon
              className={classNames('block h-4 w-4 transition-transform', isExpanded ? 'rotate-0' : '-rotate-90')}
            />
          </button>
        ) : (
          <div className="flex items-center justify-between px-0.5 py-1">
            {header}
            <RadixCollapsible.Trigger>
              <Tooltip label={toggleLabel}>
                <IconButton appearance="ghost" color="gray" label={toggleLabel} size="small">
                  <ChevronDownIcon
                    className={classNames('block h-4 w-4 transition-transform', isExpanded ? 'rotate-0' : '-rotate-90')}
                  />
                </IconButton>
              </Tooltip>
            </RadixCollapsible.Trigger>
          </div>
        )}

        <RadixCollapsible.Content className="radix-state-closed:animate-radix-collapsible-slide-up radix-state-open:animate-radix-collapsible-slide-down overflow-hidden">
          <div className={clickableHeader ? 'px-4 pt-2 pb-4' : undefined}>{children}</div>
          {footer}
        </RadixCollapsible.Content>
      </RadixCollapsible.Root>
    );
  },
);

CollapsibleCard.displayName = 'CollapsibleCard';
